import { toast } from "sonner";

const defaultOptions = {
  duration: 4000,
  position: "top-right",
};

/**
 * Toast utility wrapper for Sonner
 *
 * Refactored version with improvements:
 * - Removed boolean-to-error conversion (no longer needed with new service pattern)
 * - Simplified minDelay implementation
 * - Better error message handling
 */
export const showToast = {
  success: (message, options = {}) =>
    toast.success(message, { ...defaultOptions, ...options }),

  error: (message, options = {}) =>
    toast.error(message, { ...defaultOptions, ...options }),

  warning: (message, options = {}) =>
    toast.warning(message, { ...defaultOptions, ...options }),

  info: (message, options = {}) =>
    toast.info(message, { ...defaultOptions, ...options }),

  /**
   * Promise toast with loading, success, and error states
   *
   * NEW: Works perfectly with services that throw on error
   *
   * @param {Promise} promise - Promise to track
   * @param {Object} messages - Toast messages
   * @param {string} messages.loading - Loading message
   * @param {string|Function} messages.success - Success message or function(data) => string
   * @param {string|Function} messages.error - Error message or function(error) => string
   * @param {Object} options - Additional options
   * @param {number} options.minDelay - Minimum delay in ms (default: 500)
   *
   * @example
   * // Basic usage
   * showToast.promise(
   *   authService.login(payload),
   *   {
   *     loading: "Logging in...",
   *     success: "Welcome back!",
   *     error: (err) => err.message // Uses actual API error message
   *   }
   * );
   *
   * @example
   * // With custom success message based on response
   * showToast.promise(
   *   userService.createUser(payload),
   *   {
   *     loading: "Creating user...",
   *     success: (data) => `User ${data.name} created!`,
   *     error: (err) => err.message
   *   }
   * );
   *
   * @example
   * // Without minDelay
   * showToast.promise(
   *   service.quickAction(),
   *   {
   *     loading: "Processing...",
   *     success: "Done!",
   *     error: (err) => err.message
   *   },
   *   { minDelay: 0 }
   * );
   */
  promise: (promise, messages, options = {}) => {
    const minDelay = options.minDelay ?? 500;

    // Apply minimum delay if specified
    const delayedPromise =
      minDelay > 0
        ? Promise.all([
            promise,
            new Promise((resolve) => setTimeout(resolve, minDelay)),
          ]).then(([result]) => result)
        : promise;

    return toast.promise(delayedPromise, {
      loading: messages.loading || "Loading...",
      success: messages.success || "Success!",
      error: (error) => {
        if (typeof messages.error === "function") {
          return messages.error(error);
        }
        return messages.error || error?.message || "An error occurred";
      },
      ...options,
    });
  },
};
